import { inject } from '@angular/core';
import { ResolveFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LoginService } from '../../auth/login/login.service';
import { buildClientRoute } from '../utils/client-route.util';

/**
 * Interface for password reset token validation result
 */
export interface PasswordResetTokenValidationResult {
  email: string;
  code: string;
  isValid: boolean;
}

/**
 * Password Reset Token Resolver
 *
 * Validates the reset link before the reset-password component loads, so an already-used or
 * expired link shows the invalid-reset-link page rather than a password form that only fails
 * once the user has filled it in.
 *
 * Mirrors invitationTokenResolver. The check is a convenience only — ResetPassword re-validates
 * server-side and remains the authority, since a link can be consumed elsewhere between page
 * load and submit.
 *
 * Usage: Add `resolve: { resetToken: passwordResetTokenResolver }` to the route configuration.
 */
export const passwordResetTokenResolver: ResolveFn<PasswordResetTokenValidationResult> = (
  route: ActivatedRouteSnapshot
) => {
  const loginService = inject(LoginService);
  const router = inject(Router);

  const emailParam = route.queryParams['email'];
  const codeParam = route.queryParams['code'];

  // No link parameters at all — an authenticated user may reach this page directly to change
  // their own password, so let the component decide rather than blocking here.
  if (!emailParam && !codeParam) {
    return { email: '', code: '', isValid: false };
  }

  // Half a link is a broken link.
  if (!emailParam || !codeParam) {
    console.warn('PasswordResetTokenResolver: Missing email or code in query parameters');
    router.navigate(buildClientRoute(router, ['/invalid-reset-link']));
    return throwError(() => new Error('Missing email or code parameters'));
  }

  const decodedEmail = decodeURIComponent(emailParam);
  const decodedCode = decodeURIComponent(codeParam);

  return loginService.validateResetToken(decodedEmail, decodedCode).pipe(
    map((response: any) => {
      // Response may be unwrapped by the interceptor, so check both structures
      const isSuccess = response?.data?.success === true ||
                        response?.success === true ||
                        response?.isSuccess === true;

      if (isSuccess) {
        return {
          email: decodedEmail,
          code: decodedCode,
          isValid: true
        };
      }

      console.warn('PasswordResetTokenResolver: Reset link is invalid, expired, or already used');
      router.navigate(buildClientRoute(router, ['/invalid-reset-link']));
      const validationError = new Error('Invalid or expired password reset link');
      (validationError as any).status = 403;
      throw validationError;
    }),
    catchError((error: any) => {
      console.error('PasswordResetTokenResolver: Error validating reset link:', error);

      const statusCode = error?.status || error?.error?.statusCode || error?.statusCode;
      const errorMessage = statusCode === 400 || statusCode === 401 || statusCode === 403
        ? 'This password reset link is no longer valid. Please request a new one.'
        : error?.message || 'Failed to validate the password reset link. Please try again.';

      router.navigate(buildClientRoute(router, ['/invalid-reset-link']));

      return throwError(() => new Error(errorMessage));
    })
  );
};
